import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Chart } from '../../../../helpers/chart.js';
import { fetchJson } from '../../../../helpers';
import { useSelector } from 'react-redux';
import { useChartResize } from '../../hooks/useChartResize.js';
import { DEFAULT_COLORS } from '../base/constants.js';
import NoDataComponent from '../../no_data_component.jsx';

import {
    monitoring_dashboard_report_status_data_path
} from '~/routes';
import LoadingIndicator from '../../../common/loading_indicator';

function ReportStatusWidget({ widgetId }) {
    const reportStatusChartRef = useRef();
    const [chart, setChart] = useState();
    const [loading, setLoading] = useState(false);
    const [chartData, setChartData] = useState(null);
    const report_id = useSelector(state => state.dashboard.widgets[widgetId].settings?.report_id);
    const submit_color = useSelector(state => state.dashboard.widgets[widgetId].settings?.submit_color);
    const draft_color = useSelector(state => state.dashboard.widgets[widgetId].settings?.draft_color);
    const block_color = useSelector(state => state.dashboard.widgets[widgetId].settings?.block_color);
    const legend_text_color = useSelector(state => state.dashboard.widgets[widgetId].settings?.legend_text_color);

    const initChart = useCallback(() => {
            if (chart) chart.destroy();
            if (!reportStatusChartRef.current || !chartData) return;
            const barsDataWithColors = {
                ...chartData.bars,
                datasets: chartData.bars.datasets.map((d, i) => {
                return {
                    ...d, 
                    backgroundColor: [submit_color, draft_color, block_color][i] || DEFAULT_COLORS[i]
                }
                })
            }
            setChart(new Chart(reportStatusChartRef.current, {
                type: "bar",
                data: barsDataWithColors,
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: {
                        labels: {
                            color: legend_text_color,
                        },
                    },
                    scales: {
                        x:  {
                            stacked: true,
                            ticks: {
                                autoSkip: false,
                                maxRotation: 0,
                                padding: 10,
                                // callback: function (value, index, values) {
                                //     return value.split(" "); 
                                // }
                            },
                        },
                        y: {
                            stacked: true,
                            ticks: {
                                beginAtZero: true,
                                callback: function (value, index, values) {
                                return Number.isInteger(value) ? value : "";
                                },
                            },
                        },
                    },
                    tooltips: {
                        mode: "index",
                        callbacks: {
                            label: function (tooltipItem, data) {
                                var label = data.datasets[tooltipItem.datasetIndex].label;
                                if (
                                    data.datasets[tooltipItem.datasetIndex].optional[tooltipItem.index] ===
                                    "MonitoringReportFillPhase"
                                ) {
                                    switch (tooltipItem.datasetIndex) {
                                    case 0:
                                        label = I18n.t("submitted");
                                        break;
                                    case 1:
                                        label = I18n.t("draft");
                                        break;
                                    case 2:
                                        label = null;
                                        break;
                                    default:
                                        break;
                                    }
                                } else if (
                                    data.datasets[tooltipItem.datasetIndex].optional[tooltipItem.index] ===
                                    "MonitoringReportCommentingPhase"
                                ) {
                                    switch (tooltipItem.datasetIndex) {
                                    case 0:
                                        label = I18n.t("pass");
                                        break;
                                    case 1:
                                        label = I18n.t("draft");
                                        break;
                                    case 2:
                                        label = I18n.t("blocked");
                                        break;
                                    default:
                                        break;
                                    }
                                } else {
                                    switch (tooltipItem.datasetIndex) {
                                    case 0:
                                        label = I18n.t("approved");
                                        break;
                                    case 1:
                                        label = I18n.t("assigned");
                                        break;
                                    case 2:
                                        label = null;
                                        break;
                                    default:
                                        break;
                                    }
                                }
                                if (label) {
                                    label += ": ";
                                }
                                label += Math.round(tooltipItem.yLabel * 100) / 100;
                                return label;
                            },
                        },
                    },
                },
            }))
    }, [chartData, submit_color, draft_color, block_color, legend_text_color])

    const handleResize = useChartResize(reportStatusChartRef, initChart);

    const loadData = (reportId) => {
        if (chart) chart.destroy();
        setLoading(true);
        fetchJson(monitoring_dashboard_report_status_data_path({ report_id: reportId })).then((res) => {
            setLoading(false);
            setChartData(res)
        })
    }
    useEffect(() => {
        return () => {
            if (chart) chart.destroy();
        }
    }, [])

    useEffect(() => {
        if (report_id){
            loadData(report_id)
        }
    }, [report_id])

    useEffect(() => {
        if (!loading && chartData){
            handleResize();
        }
    }, [chartData, submit_color, draft_color, block_color, legend_text_color])
    
    return (
        loading ? <LoadingIndicator /> :
        ( report_id ? 
        <div className='chart-container-gen2'>
            <canvas ref={reportStatusChartRef} />
        </div> : <NoDataComponent />)
    )
}

export default ReportStatusWidget;
